<template>
  <div class="temp-payments">
    <h2>การชำระเงินชั่วคราว (Temp Payments)</h2>
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>กำลังโหลดข้อมูล...</p>
    </div>
    <div v-else-if="isProcessing" class="loading">
      <div class="spinner"></div>
      <p>กำลังดำเนินการ...</p>
    </div>
    <div v-else-if="tempPayments.length === 0" class="no-data">
      ไม่มีการชำระเงินชั่วคราวที่รอการอนุมัติ
    </div>
    <div v-else class="payment-list">
      <div v-for="payment in tempPayments" :key="payment.bookingCode" class="payment-card">
        <div class="payment-header">
          <h3>รหัสการจอง: {{ payment.bookingCode }}</h3>
          <span v-if="payment.status === 'pending' && !payment.slipUrl" class="status waiting">รอการอัพโหลดสลิป</span>
          <span v-else-if="payment.status === 'pending'" class="status pending">รอการอนุมัติ</span>
          <span v-else-if="payment.status === 'completed'" class="status completed">อนุมัติแล้ว</span>
          <span v-else-if="payment.status === 'rejected'" class="status rejected">ปฏิเสธแล้ว</span>
          <span v-else class="status unknown">ไม่ทราบสถานะ</span>
        </div>
        <div class="payment-details">
          <p><strong>จำนวนเงิน:</strong> {{ payment.amount }} บาท</p>
          <p><strong>วันที่อัพโหลด:</strong> {{ formatDate(payment.uploadedAt) }}</p>
        </div>
        <div class="payment-actions">
          <button @click="viewPaymentSlip(payment)" class="btn view-btn">
            ดูสลิป
          </button>
          <button @click="approvePayment(payment)" class="btn approve-btn" :disabled="isProcessing">
            อนุมัติ
          </button>
          <button @click="rejectPayment(payment)" class="btn reject-btn" :disabled="isProcessing">
            ปฏิเสธ
          </button>
        </div>
      </div>
    </div>

    <!-- Payment Slip Modal -->
    <div v-if="showSlipModal" class="modal">
      <div class="modal-content">
        <span class="close" @click="showSlipModal = false">&times;</span>
        <h2>สลิปการชำระเงิน</h2>
        <img :src="selectedSlipUrl" alt="Payment Slip" class="slip-image">
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { collection, query, getDocs, updateDoc, doc, addDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { auth } from '../firebase/firebaseConfig';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

export default {
  name: 'TempPayments',
  setup() {
    const loading = ref(true);
    const tempPayments = ref([]);
    const showSlipModal = ref(false);
    const selectedSlipUrl = ref('');
    const isProcessing = ref(false);

    // Fetch temp payments
    const fetchTempPayments = async () => {
      try {
        // แสดงทั้งสถานะ pending และ completed
        const q = query(collection(db, 'temp_payments'));
        const querySnapshot = await getDocs(q);
        tempPayments.value = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('Error fetching temp payments:', error);
        alert('เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + error.message);
      }
    };

    // Format date
    const formatDate = (timestamp) => {
      if (!timestamp) return '';
      const date = timestamp.toDate();
      return date.toLocaleString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // View payment slip
    const viewPaymentSlip = (payment) => {
      selectedSlipUrl.value = payment.slipUrl;
      showSlipModal.value = true;
    };

    // Approve payment
    const approvePayment = async (payment) => {
      if (!confirm('คุณต้องการอนุมัติการชำระเงินนี้ใช่หรือไม่?')) return;
      
      isProcessing.value = true;
      
      try {
        const storage = getStorage();
        
        // รับนามสกุลไฟล์จาก URL
        let fileExtension = 'jpg';
        try {
          fileExtension = payment.slipUrl.split('.').pop().split('?')[0] || 'jpg';
        } catch (e) {
          console.warn('Could not determine file extension, using jpg as default');
        }
        
        // สร้าง path ใหม่ในโฟลเดอร์ payments
        const newPath = `payments/${payment.bookingCode}_${Date.now()}.${fileExtension}`;
        const newRef = storageRef(storage, newPath);
        
        // ดาวน์โหลดและอัปโหลดในขั้นตอนเดียว
        const response = await fetch(payment.slipUrl);
        if (!response.ok) throw new Error('ไม่สามารถดาวน์โหลดสลิปได้');
        
        const blob = await response.blob();
        await uploadBytes(newRef, blob);
        const newUrl = await getDownloadURL(newRef);
        
        // สร้างข้อมูลใหม่ใน payment_requests
        const paymentData = {
          bookingCode: payment.bookingCode,
          amount: payment.amount,
          paymentReceipt: newUrl,
          status: 'completed',
          approvedAt: new Date(),
          approvedBy: auth.currentUser.email,
          createdAt: new Date()
        };

        // เพิ่มข้อมูลใน payment_requests
        await addDoc(collection(db, 'payment_requests'), paymentData);

        // อัพเดทสถานะใน temp_payments 
        const tempPaymentRef = doc(db, 'temp_payments', payment.bookingCode);
        await updateDoc(tempPaymentRef, {
          status: 'completed',
          approvedAt: new Date(),
          approvedBy: auth.currentUser.email
        });

        // อัพเดทสถานะใน bookings
        const bookingRef = doc(db, 'bookings', payment.bookingCode);
        await updateDoc(bookingRef, {
          depositPaid: true,
          depositPaidAt: new Date()
        });

        alert('อนุมัติการชำระเงินเรียบร้อย');
        
        // รีเฟรชข้อมูล
        await fetchTempPayments();
      } catch (error) {
        console.error('Error approving payment:', error);
        alert('เกิดข้อผิดพลาดในการอนุมัติการชำระเงิน: ' + error.message);
      } finally {
        isProcessing.value = false;
      }
    };

    // Reject payment
    const rejectPayment = async (payment) => {
      if (!confirm('คุณต้องการปฏิเสธการชำระเงินนี้ใช่หรือไม่?')) return;

      isProcessing.value = true;
      
      try {
        // อัพเดทสถานะใน temp_payments 
        const tempPaymentRef = doc(db, 'temp_payments', payment.bookingCode);
        await updateDoc(tempPaymentRef, {
          status: 'rejected',
          rejectedAt: new Date(),
          rejectedBy: auth.currentUser.email
        });

        alert('ปฏิเสธการชำระเงินเรียบร้อย');
        
        // รีเฟรชข้อมูล
        await fetchTempPayments();
      } catch (error) {
        console.error('Error rejecting payment:', error);
        alert('เกิดข้อผิดพลาดในการปฏิเสธการชำระเงิน: ' + error.message);
      } finally {
        isProcessing.value = false;
      }
    };

    // Initial data fetch
    onMounted(async () => {
      try {
        await fetchTempPayments();
      } finally {
        loading.value = false;
      }
    });

    return {
      loading,
      tempPayments,
      showSlipModal,
      selectedSlipUrl,
      isProcessing,
      formatDate,
      viewPaymentSlip,
      approvePayment,
      rejectPayment
    };
  }
};
</script>

<style scoped>
.temp-payments {
  padding: 20px;
}

.loading {
  text-align: center;
  padding: 20px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #666;
  background: #f5f5f5;
  border-radius: 8px;
}

.payment-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.payment-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.payment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.payment-header h3 {
  margin: 0;
  color: #333;
}

.status {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: bold;
  text-align: center;
}

.waiting {
  background-color: #ccc;
  color: #333;
}

.pending {
  background-color: #ffc107;
  color: #333;
}

.completed {
  background-color: #28a745;
  color: white;
}

.rejected {
  background-color: #dc3545;
  color: white;
}

.unknown {
  background-color: #6c757d;
  color: white;
}

.payment-details {
  margin-bottom: 15px;
}

.payment-details p {
  margin: 5px 0;
  color: #666;
}

.payment-actions {
  display: flex;
  gap: 10px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.view-btn {
  background: #e9ecef;
  color: #495057;
}

.approve-btn {
  background: #28a745;
  color: white;
}

.reject-btn {
  background: #dc3545;
  color: white;
}

.btn:hover {
  opacity: 0.9;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 90%;
  max-height: 90vh;
  position: relative;
}

.close {
  position: absolute;
  right: 20px;
  top: 10px;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.slip-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}
</style>